import { Box } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import { TextWrapper } from '../../../../../shared/components/formUtilities/useTextWrapper';
import { FormikProvider, useFormik } from 'formik';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { IS_SA } from '../../../../../config/appConfig';
import { OTPTypes } from '../../../../../constants/otpTypes';
import { FormHelper } from '../../../../../helpers/formHelper';
import { SystemSettingsService } from '../../../../../services/systemSettingsService';
import { SectionWithHeading } from '../../../../../shared/components/common/sectionWithHeading';
import { ConfirmPasswordDialog } from '../../../../../shared/components/confirmPasswordDialog';
import { DatePickerWrapper } from '../../../../../shared/components/formUtilities/useDatePicker';
import { InputWrapper } from '../../../../../shared/components/formUtilities/useInput';
import { InternationalPhone } from '../../../../../shared/components/formUtilities/useInternationalPhone';
import SelectWrapper from '../../../../../shared/components/formUtilities/useSelect';
import { OTPAuthenticationDialog } from '../../../../../shared/components/otpAuthenticationDialog';
import { SubscriptionChangeDialog } from '../../../../../shared/components/subscriptionChangeDialog';
import { InvestorProfileUpdateKSASchema, InvestorProfileUpdateSchema } from '../../../../../shared/validations/schema';
import { InvestorService } from '../../../investorService';

export const InvestorPersonalInfo = ({
	investorDetails,
	onSavePersonalInfo = () => { },
	isSubscribed = null,
	subscriptionLoading = false,
	onSubscriptionChange = () => { },
}) => {
	const systemSettingsService = new SystemSettingsService();
	const investorService = new InvestorService();
	const { investorCollections } = useSelector(
		(state) => state.investorCollectionReducer
	);
	const { t } = useTranslation();
	const [isEditInfo, setIsEditInfo] = useState(false);
	const [loading, setLoading] = useState(false);
	const [showPasswordDialog, setShowPasswordDialog] = useState(false);
	const [showOTPAuth, setShowOTPAuth] = useState(false);
	const [openSubscriptionDialog, setOpenSubscriptionDialog] = useState(false);

	const countryList = investorCollections?.countryCollection || [];
	const employmentStatusList =
		investorCollections?.employmentStatusCollection || [];
	const industryList = investorCollections?.industriesCollection || [];
	const investmentSourceList =
		investorCollections?.incomeSourceCollection || [];
	const annualIncomeList = investorCollections?.annualIncomeCollection || [];
	const publicFunctionList =
		investorCollections?.publicFunctionCollection || [];
	const netWorthList = investorCollections?.netWorthCollection || [];
	const investorTypeList = investorCollections?.investorTypeCollection || [];
	const estimatedYearlyInvestments =
		investorCollections?.estimateAnnualInvestmentCollection || [];
	const hearAboutUsList = investorCollections?.hearUsItemCollection || [];
	const professionalLevels =
		investorCollections?.professionalLevels || [];
	const industrySectors =
		investorCollections?.industrySectors || [];
	const crsWhyNotQuestionsList = investorCollections?.crsWhyNotQuestionsCollection || [];

	const form = useFormik({
		initialValues: {
			investorTypeId: '',
			firstName: '',
			lastName: '',
			placeOfBirthId: '',
			birthDate: '',
			netWorthId: '',
			nationalityId: '',
			contactNumber: '',
			nationalId: '',
			bankCountryId: '',
			placeOfBirth: '', // TODO: add validation if needed
			employmentStatusId: '',
			companyName: '',
			annualIncomeId: '',
			estimatedAnnualInvestmentId: '',
			professionalLevelId: "",
			industrySectorId: "",
			address: FormHelper.buildAddressFormGroup(),
			industry: FormHelper.buildOtherFormGroup(),
			incomeSource: FormHelper.buildOtherFormGroup(),
			publicFunction: FormHelper.buildOtherFormGroup(),
			jobTitle: '',
			taxResidencies: FormHelper.getTaxResidencies(),
			howDidHearAboutUs: FormHelper.buildOtherFormGroup(),
		},
		validationSchema: IS_SA ? InvestorProfileUpdateKSASchema : InvestorProfileUpdateSchema,
		onSubmit: () => {
			if (settings()?.enabledOtp) {
				setShowOTPAuth(true);
			} else {
				setShowPasswordDialog(true);
			}
		},
		...FormHelper.formikProps(),
	});

	const settings = () => {
		return systemSettingsService.systemSettings();
	};

	useEffect(() => {
		let formValues = {
			...investorDetails,
			investorTypeId: investorDetails?.investorType?.id,
			nationalityId: investorDetails?.nationality?.id,
			employmentStatusId: investorDetails?.employmentStatus?.id,
			annualIncomeId: investorDetails?.annualIncome?.id,
			jobTitle: investorDetails?.jobTitle,
			netWorthId: investorDetails?.netWorth?.id,
			bankCountryId: investorDetails?.bankCountry?.id,
			professionalLevelId: investorDetails?.professionalLevel?.id || null,
			industrySectorId: investorDetails?.industrySector?.id || null,
			placeOfBirthId: investorDetails?.placeOfBirthId?.id || null,
			estimatedAnnualInvestmentId:
				investorDetails?.estimatedAnnualInvestment?.id,
			industry: FormHelper.getComplexOther(investorDetails?.industry),
			incomeSource: FormHelper.getComplexOther(investorDetails?.incomeSource),
			howDidHearAboutUs: FormHelper.getComplexOther(investorDetails?.howDidHearAboutUs),
			publicFunction: FormHelper.getComplexOther(
				investorDetails?.publicFunction
			),
			address: {
				...investorDetails?.address,
				addressLine: investorDetails.address?.addressLine || null,
				additionalAddressLine:
					investorDetails.address?.additionalAddressLine || null,
				city: investorDetails.address?.city || null,
				postalCode: investorDetails.address?.postalCode || null,
				countryId: investorDetails.address?.country?.id || null,
			},
			taxResidencies: FormHelper.getTaxResidencies(investorDetails?.taxResidencies),
		};
		form.setValues(formValues);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [investorDetails]);

	const onEdit = (e) => {
		e.preventDefault();
		if (isEditInfo) {
			FormHelper.validateForm(form);
		} else {
			setIsEditInfo(!isEditInfo);
		}
	};

	const onSaveInfo = () => {
		setLoading(true);
		const personalInfo = form.values;
		delete personalInfo.occupation;
		delete personalInfo.status;
		delete personalInfo.employmentStatus;
		if (IS_SA) {
			delete personalInfo.address;
		}

		investorService
			.updateInvestorProfile(personalInfo)
			.then((resp) => {
				onSavePersonalInfo();
				setLoading(false);
				setIsEditInfo(!isEditInfo);
			})
			.catch((err) => {
				setLoading(false);
			});
	};

	const onCloseOTPAuth = (e) => {
		setShowOTPAuth(false);
		if (e) {
			onSaveInfo();
		}
	};

	const onPasswordDialogClose = (e) => {
		setShowPasswordDialog(false);
		if (e) {
			onSaveInfo();
		}
	};

	const checkPlaceOfBirth = () => {
		if (investorDetails?.placeOfBirthId) {
			return true;
		}
		if (investorDetails?.placeOfBirth && !investorDetails?.placeOfBirthId) {
			return false;
		}
		return true;
	};

	const checkEmpStatusVisibility = () => {
		if (investorDetails?.professionalLevel?.id) {
			return true;
		}
		if (!investorDetails?.professionalLevel?.id && investorDetails?.employmentStatus?.id) {
			return false;
		}
		return true;
	};

	const checkIndustryVisibility = () => {
		if (investorDetails?.industrySector?.id) {
			return true;
		}
		if (!investorDetails?.industrySector?.id && investorDetails?.industry?.valueId) {
			return false;
		}
		return true;
	};

	return (
		<SectionWithHeading
			loading={loading}
			isEditButton={true}
			onEditInfo={(e) => {
				onEdit(e);
			}}
			isEditToggle={isEditInfo}
			heading={
				<Box className="flex items-center">
					<span className="mx-2">{t('information')}</span>
				</Box>
			}
			children={
				<Box>
					<fieldset disabled={!isEditInfo}>
						<FormikProvider value={form}>
							<Box className="grid grid-cols-1 md:grid-cols-3 md:gap-3 gap-2">
								<SelectWrapper
									disabled={true}
									name={'investorTypeId'}
									key={'investorTypeId'}
									items={investorTypeList}
									label={t('investorType')}
								/>
								<InputWrapper
									disabled={true}
									name={'firstName'}
									key={'firstName'}
									label={t('firstName')}></InputWrapper>
								<InputWrapper
									disabled={true}
									name={'lastName'}
									key={'lastName'}
									label={t('lastName')}></InputWrapper>
								<DatePickerWrapper
									disabled={true}
									defaultMaxDate={true}
									name={'birthDate'}
									key={'birthDate'}
									label={t('dateOfBirth')}></DatePickerWrapper>
								<SelectWrapper
									disabled={true}
									name={'nationalityId'}
									key={'nationalityId'}
									items={countryList}
									label={t('nationality')}
								/>
								{checkPlaceOfBirth() ? <SelectWrapper
									disabled
									dataTestID='placeOfBirthId'
									errorStateTestID="placeOfBirthId"
									name={'placeOfBirthId'}
									key={'placeOfBirthId'}
									items={countryList}
									label={'placeOfBirth'}
								/> : <InputWrapper
									disabled
									dataTestID='placeOfBirth'
									errorStateTestID="placeOfBirthError"
									name={'placeOfBirth'}
									key={'placeOfBirth'}
									label={'placeOfBirth'}></InputWrapper>}
								{/* <InputWrapper
									disabled={true}
									name={'placeOfBirth'}
									key={'placeOfBirth'}
									label={t('placeOfBirth')}></InputWrapper> */}
								<InternationalPhone
									phoneWithCode={investorDetails?.contactNumber}
									name={'contactNumber'}
									key={'contactNumber'}
									label={'contactNumber'}></InternationalPhone>
								<InputWrapper
									inputClass='disabled !pointer-events-none'
									name={'nationalId'}
									key={'nationalId'}
									label={t('nationalIdPassportNo')}></InputWrapper>
								{checkEmpStatusVisibility() ? <SelectWrapper
									externalErrorMessage={!form.values?.professionalLevelId ? 'required' : ''}
									errorStateTestID="professionalLevelIdError"
									name={'professionalLevelId'}
									key={'professionalLevelId'}
									items={professionalLevels}
									label={'professionalLevel'}
								/> : <SelectWrapper
									externalErrorMessage={!form.values?.employmentStatusId ? 'required' : ''}
									errorStateTestID="employmentStatusIdError"
									name={'employmentStatusId'}
									key={'employmentStatusId'}
									items={employmentStatusList}
									label={'employmentDetails'}
								/>}
								<InputWrapper
									name={'companyName'}
									key={'companyName'}
									label={t('companyName')}></InputWrapper>
								<SelectWrapper
									name={'annualIncomeId'}
									key={'annualIncomeId'}
									items={annualIncomeList}
									label={t('annualIncome')}
								/>
								<InputWrapper
									name={'jobTitle'}
									key={'jobTitle'}
									label={t('jobTitle')}></InputWrapper>
								<SelectWrapper
									tooltipId={'networthProfile'}
									tooltipText={'netWorthTooltipText'}
									name={'netWorthId'}
									key={'netWorthId'}
									items={netWorthList}
									label={t('netWorth')}
								/>
								<SelectWrapper
									name={'estimatedAnnualInvestmentId'}
									key={'estimatedAnnualInvestmentId'}
									items={estimatedYearlyInvestments}
									label={'estimatedYearlyInvestment'}
								/>
								<SelectWrapper
									name={'bankCountryId'}
									key={'bankCountryId'}
									items={countryList}
									label={'bankCountry'}
								/>
								{checkIndustryVisibility() ? <>
									<SelectWrapper
										externalErrorMessage={!form.values?.industrySectorId ? 'required' : ''}
										name={'industrySectorId'}
										key={'industrySectorId'}
										items={industrySectors}
										label={'industry'}
									/>
								</> : <>
									<SelectWrapper
										name={'industry.valueId'}
										key={'industry.valueId'}
										controlOtherValueName={'industry.valueSpecified'}
										items={industryList}
										label={t('industry')}
									/></>}
								<SelectWrapper
									name={'incomeSource.valueId'}
									key={'incomeSource.valueId'}
									controlOtherValueName={'incomeSource.valueSpecified'}
									items={investmentSourceList}
									label={t('fundingSourceOfInvestment')}
								/>
								<SelectWrapper
									valueSpecifiedNames={['Other', 'Influencer']}
									name={'howDidHearAboutUs.valueId'}
									key={'howDidHearAboutUs.valueId'}
									controlOtherValueName={'howDidHearAboutUs.valueSpecified'}
									items={hearAboutUsList}
									label={'howHearAboutUs'}
								/>
								{!IS_SA ? (
									<>
										<SelectWrapper
											name={'publicFunction.valueId'}
											key={'publicFunction.valueId'}
											controlOtherValueName={'publicFunction.valueSpecified'}
											items={publicFunctionList}
											label={t('publicFunction')}
										/>
										<InputWrapper
											name={'address.addressLine'}
											key={'address.addressLine'}
											label={t('address')}></InputWrapper>
										<SelectWrapper
											name={'address.countryId'}
											key={'address.countryId'}
											items={countryList}
											label={t('countryOfResidence')}
										/>
									</>
								) : null}
								<SelectWrapper
									items={countryList}
									name={`taxResidencies.${0}.countryId`}
									key={`taxResidencies.${0}.countryId`}
									label={t('taxResidency')}
								/>
								<SelectWrapper
									items={[{ name: "Yes", displayName: "Yes", id: true },
									{ name: "No", displayName: "No", id: false }
									]}
									name={`taxResidencies.${0}.hasTaxId`}
									key={`taxResidencies.${0}.hasTaxId`}
									label={t('doYouHaveTaxIdInThisCountry')}
								/>
								<Box className='flex items-center'>
									{form.values.taxResidencies[0]?.hasTaxId === true ? (
										<InputWrapper
											name={`taxResidencies.${0}.taxId`}
											key={`taxResidencies.${0}.taxId`}
											label={t('taxId')}></InputWrapper>
									) : form.values.taxResidencies[0]?.hasTaxId === false ? (
										<SelectWrapper
											items={crsWhyNotQuestionsList}
											name={`taxResidencies.${0}.whyNotId`}
											key={`taxResidencies.${0}.whyNotId`}
											label={t('whyNot')}
										/>
									) : null}
								</Box>
							</Box>
								<Box className="flex flex-col items-start ml-5">
									<Box className="flex flex-0 items-center w-full relative mb-1">
										<TextWrapper
											classList="truncate-word"
											type="caption"
											text={t('subscribedToOurEmailList') || 'Subscribed to our email list'}
										/>
										<a
											href="/"
											onClick={(e) => {
												e.preventDefault();
												setOpenSubscriptionDialog(true);
											}}
											className="cursor-pointer ml-2">
											<EditIcon sx={{ fontSize: 18}} />
										</a>
									</Box>
									<TextWrapper
										classList="truncate-word"
										type="body2"
										text={subscriptionLoading ? 'Loading...' : (isSubscribed === null ? '' : (isSubscribed ? 'Subscribed' : 'Not Subscribed'))}
									/>
								</Box>
						</FormikProvider>
					</fieldset>
					{showOTPAuth ? (
						<OTPAuthenticationDialog
							type={OTPTypes.Other}
							state={showOTPAuth}
							onClose={(e) => {
								onCloseOTPAuth(e);
							}}></OTPAuthenticationDialog>
					) : null}
					{showPasswordDialog ? (
						<ConfirmPasswordDialog
							state={showPasswordDialog}
							onClose={(e) => {
								onPasswordDialogClose(e);
							}}></ConfirmPasswordDialog>
					) : null}
					{openSubscriptionDialog && (
						<SubscriptionChangeDialog
							state={openSubscriptionDialog}
							name={investorDetails?.firstName || investorDetails?.fullName || 'User'}
							subscribed={isSubscribed}
							onClose={(shouldClose, subscriptionValue) => {
								setOpenSubscriptionDialog(false);
								if (subscriptionValue !== null && subscriptionValue !== undefined && onSubscriptionChange) {
									onSubscriptionChange(subscriptionValue);
								}
							}}
						/>
					)}
				</Box>
			}></SectionWithHeading>
	);
};
